import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { apiService } from "../../api/services/apiService";
import type { AppKyc } from "../../types";
import { AppKeyStatus } from "../../types";
import { fromDatetimeLocalValue, toDatetimeLocalValue } from "../../utils/appKycDate";

interface EditAppKycDialogProps {
  appKyc: AppKyc | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: AppKyc) => void;
}

const EditAppKycDialog: React.FC<EditAppKycDialogProps> = ({
  appKyc,
  open,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<AppKyc | null>(appKyc);
  const [keyCreatedOn, setKeyCreatedOn] = useState("");
  const [keyExpiresOn, setKeyExpiresOn] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(appKyc);
    setKeyCreatedOn(toDatetimeLocalValue(appKyc?.keyCreatedOn));
    setKeyExpiresOn(toDatetimeLocalValue(appKyc?.keyExpiresOn));
  }, [appKyc]);

  const handleSave = async () => {
    if (!draft?.uuid) return;
    if (
      !draft.appKeyId?.trim() ||
      !draft.providerGatewayType?.trim() ||
      !draft.providerGatewayIdentifier?.trim()
    ) {
      alert("App Key ID, Provider Gateway Type, and Provider Gateway Identifier are required.");
      return;
    }
    setSaving(true);
    try {
      const updated = await apiService.updateAppKyc(draft.uuid, {
        appKeyId: draft.appKeyId.trim(),
        providerGatewayType: draft.providerGatewayType.trim(),
        providerGatewayIdentifier: draft.providerGatewayIdentifier.trim(),
        appKeyStatus: draft.appKeyStatus,
        description: draft.description ?? undefined,
        keyCreatedOn: fromDatetimeLocalValue(keyCreatedOn),
        keyExpiresOn: fromDatetimeLocalValue(keyExpiresOn),
        contactName: draft.contactName ?? undefined,
        contactEmail: draft.contactEmail ?? undefined,
        contactPhone: draft.contactPhone ?? undefined,
        contactCompanyName: draft.contactCompanyName ?? undefined,
        contactCompanyWebsite: draft.contactCompanyWebsite ?? undefined,
        bmContactName: draft.bmContactName ?? undefined,
        bmContactEmail: draft.bmContactEmail ?? undefined,
        itSupportName: draft.itSupportName ?? undefined,
        itSupportEmail: draft.itSupportEmail ?? undefined,
      });
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update App KYC");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit App KYC</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          App key & gateway *
        </Typography>
        <TextField
          fullWidth
          label="App Key ID *"
          margin="normal"
          value={draft?.appKeyId ?? ""}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, appKeyId: e.target.value } : prev))
          }
        />
        <TextField
          fullWidth
          label="Provider Gateway Type *"
          margin="normal"
          value={draft?.providerGatewayType ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, providerGatewayType: e.target.value } : prev,
            )
          }
        />
        <TextField
          fullWidth
          label="Provider Gateway Identifier *"
          margin="normal"
          value={draft?.providerGatewayIdentifier ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, providerGatewayIdentifier: e.target.value } : prev,
            )
          }
        />
        <TextField
          select
          fullWidth
          label="App Key Status *"
          margin="normal"
          value={draft?.appKeyStatus ?? AppKeyStatus.ACTIVE}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, appKeyStatus: e.target.value as AppKeyStatus } : prev,
            )
          }
        >
          {Object.values(AppKeyStatus).map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          label="Description"
          margin="normal"
          multiline
          rows={2}
          value={draft?.description ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, description: e.target.value } : prev,
            )
          }
        />
        <TextField
          fullWidth
          label="Key Created On"
          type="datetime-local"
          margin="normal"
          value={keyCreatedOn}
          onChange={(e) => setKeyCreatedOn(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          label="Key Expires On"
          type="datetime-local"
          margin="normal"
          value={keyExpiresOn}
          onChange={(e) => setKeyExpiresOn(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Accordion sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>Contact information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              label="Contact Name"
              margin="normal"
              value={draft?.contactName ?? ""}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, contactName: e.target.value } : prev,
                )
              }
            />
            <TextField
              fullWidth
              label="Contact Email"
              margin="normal"
              value={draft?.contactEmail ?? ""}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, contactEmail: e.target.value } : prev,
                )
              }
            />
            <TextField
              fullWidth
              label="Contact Phone"
              margin="normal"
              value={draft?.contactPhone ?? ""}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, contactPhone: e.target.value } : prev,
                )
              }
            />
            <TextField
              fullWidth
              label="Company Name"
              margin="normal"
              value={draft?.contactCompanyName ?? ""}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, contactCompanyName: e.target.value } : prev,
                )
              }
            />
            <TextField
              fullWidth
              label="Company Website"
              margin="normal"
              value={draft?.contactCompanyWebsite ?? ""}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, contactCompanyWebsite: e.target.value } : prev,
                )
              }
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>Provider metadata</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              label="BM Contact Name"
              margin="normal"
              value={draft?.bmContactName ?? ""}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, bmContactName: e.target.value } : prev,
                )
              }
            />
            <TextField
              fullWidth
              label="BM Contact Email"
              margin="normal"
              value={draft?.bmContactEmail ?? ""}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, bmContactEmail: e.target.value } : prev,
                )
              }
            />
            <TextField
              fullWidth
              label="IT Support Name"
              margin="normal"
              value={draft?.itSupportName ?? ""}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, itSupportName: e.target.value } : prev,
                )
              }
            />
            <TextField
              fullWidth
              label="IT Support Email"
              margin="normal"
              value={draft?.itSupportEmail ?? ""}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, itSupportEmail: e.target.value } : prev,
                )
              }
            />
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAppKycDialog;
